import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import { ArrowPathIcon, CheckCircleIcon, ClockIcon, ExclamationCircleIcon, SignalIcon, SignalSlashIcon } from '@heroicons/react/24/outline';
import { Button } from '@mes-amis/galette-design-system';
import { dispatchDataEvent, DataEvents } from '@/db/events';
import { countPendingDocuments, listDocumentSyncErrors } from '@/db/services/document_service';
import { countPendingContacts } from '@/db/services/contact_service';
import { countPendingCaseNotes, listCaseNoteSyncErrors, type CaseNoteSyncError } from '@/db/services/case_note_service';
import type { CaseNote } from '@/db/types/case_note';
import { trackEvent } from '@/db/services/analytics_service';
import type { Document } from '@/db/types/document';
import { useServerOnline } from '@/hooks/useServerOnline';
import { describeSyncError, describeDocumentSyncError } from '@/lib/sync-errors';
import { describePending } from '@/lib/sync-copy';
import { applyServiceWorkerUpdate, checkForAppUpdate, type AppUpdateCheckResult } from '@/lib/service-worker';
import { shortAppVersion } from '@/config/env';
import { useCurrentPerson } from '@/hooks/useCurrentPerson';
import { useSyncActivity } from '@/hooks/useSyncActivity';

/** Upper bound for the manual Sync now control. */
export const MANUAL_SYNC_TIMEOUT_MS = 60_000;

function formatNoteDate(date: string): string {
  return new Date(`${date}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatWhen(date: Date | null): string {
  if (!date) return 'Never';
  return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

/**
 * Settings > Sync. Sync is automatic; this tab exists so support can see what
 * the app is doing and, when something is genuinely wrong, force a sync and
 * get a clear error message.
 */
export default function SyncTab() {
  const { organizationId } = useCurrentPerson();
  const navigate = useNavigate();
  const activity = useSyncActivity();
  const online = useServerOnline();
  const [manualError, setManualError] = useState<string | null>(null);
  const [manualRunning, setManualRunning] = useState(false);
  const [updateCheck, setUpdateCheck] = useState<'idle' | 'checking' | AppUpdateCheckResult>('idle');
  const [reloading, setReloading] = useState(false);

  const checkForUpdates = async () => {
    setUpdateCheck('checking');
    setUpdateCheck(await checkForAppUpdate());
  };

  const installUpdate = () => {
    setReloading(true);
    applyServiceWorkerUpdate(); // the page reloads once the new build takes over
  };

  // A later successful (automatic) sync supersedes an earlier manual failure.
  useEffect(() => {
    if (activity.status === 'success') setManualError(null);
  }, [activity.status, activity.finishedAt]);

  const pending = useLiveQuery(async () => {
    if (!organizationId) return { documents: 0, contacts: 0, notes: 0, errors: [], noteErrors: [] as CaseNoteSyncError[] };
    const [documents, contacts, notes, errors, noteErrors] = await Promise.all([
      countPendingDocuments(organizationId),
      countPendingContacts(organizationId),
      countPendingCaseNotes(organizationId),
      // Isolated so a failure here (the v43 index not yet present because
      // another tab is holding the upgrade) cannot take the pending counts
      // down with it.
      listDocumentSyncErrors(organizationId).catch(() => []),
      listCaseNoteSyncErrors(organizationId).catch(() => [] as CaseNoteSyncError[]),
    ]);
    return { documents, contacts, notes, errors, noteErrors };
  }, [organizationId]);

  const syncErrors = pending?.errors ?? [];
  const noteErrors = pending?.noteErrors ?? [];

  // Report each distinct message the first time this tab shows it, so the reason
  // a document is stuck exists somewhere other than this device.
  // `document.sync.failure` records it at the moment of rejection; this records
  // that a human was actually shown it. The ref is per mount, so re-entering the
  // tab reports the rows again — these are views, not unique blocks (that is
  // what `document.sync.blocked` is for).
  const reportedErrors = useRef(new Set<string>());
  useEffect(() => {
    for (const error of syncErrors) {
      const key = `${error.documentId}:${error.message}`;
      if (reportedErrors.current.has(key)) continue;
      reportedErrors.current.add(key);
      trackEvent('document.sync_error.viewed', {
        document_id: error.documentId,
        client_id: error.clientId,
        sync_error_type: error.syncErrorType,
        error_message: error.message,
        blocked: error.blocked,
      }).catch(() => {}); // analytics must never break the support screen
    }
  }, [syncErrors]);

  // A forced retry that re-fails leaves the row looking identical, so without a
  // pending state and an outcome the button reads as inert — the same loop
  // `syncNow` below already closes for the whole-app sync.
  const [retrying, setRetrying] = useState<number | string | null>(null);
  const [retryOutcome, setRetryOutcome] = useState<{ id: number | string; message: string } | null>(null);

  const retryDocument = async (documentId: number | string) => {
    setRetrying(documentId);
    setRetryOutcome(null);
    try {
      // force: the whole point of this button is to re-send a payload the server
      // already refused, after the user believes they have fixed it.
      const synced = await dispatchDataEvent<typeof DataEvents.SYNC_DOCUMENT, Document | null>(
        DataEvents.SYNC_DOCUMENT,
        { documentId, force: true }
      );
      if (synced?.sync_error_message) {
        // Not necessarily the same refusal — a retry can land on a terminal
        // outcome (deleted or completed online), where "refused it again" would
        // be wrong. Report what came back rather than assuming.
        setRetryOutcome({ id: documentId, message: describeDocumentSyncError(synced.sync_error_message) });
      } else if (synced?.synced) {
        setRetryOutcome({ id: documentId, message: 'Uploaded.' });
      } else {
        // syncDocument returns null when offline or when another sync already
        // has this document; say so rather than stopping the spinner in silence.
        setRetryOutcome({ id: documentId, message: 'Nothing was sent — try again in a moment.' });
      }
    } catch (error) {
      setRetryOutcome({ id: documentId, message: describeSyncError(error) });
    } finally {
      setRetrying(null);
    }
  };

  const retryNote = async (noteId: number | string) => {
    // Keyed apart from document ids, which share the numeric space.
    const key = `note:${noteId}`;
    setRetrying(key);
    setRetryOutcome(null);
    try {
      const result = await dispatchDataEvent<typeof DataEvents.SYNC_CASE_NOTE, CaseNote | null>(
        DataEvents.SYNC_CASE_NOTE,
        { noteId }
      );
      if (result?.synced) {
        setRetryOutcome({ id: key, message: 'Uploaded.' });
      } else if (result?.sync_error_message) {
        setRetryOutcome({ id: key, message: result.sync_error_message });
      } else {
        setRetryOutcome({ id: key, message: 'Nothing was sent — try again in a moment.' });
      }
    } catch (error) {
      setRetryOutcome({ id: key, message: describeSyncError(error) });
    } finally {
      setRetrying(null);
    }
  };

  const syncNow = async () => {
    setManualError(null);
    setManualRunning(true);
    // A dispatch never settles if the data layer is not listening; don't
    // leave a support control spinning forever.
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      // ignoreCache: this button exists for "it looks out of date". Drop every
      // stored validator first so no page can answer 304; the walk then fetches
      // and reconciles everything in full.
      const dispatch = dispatchDataEvent(DataEvents.REFRESH, { force: true, ignoreCache: true });
      dispatch.catch(() => {}); // still observed if the timeout wins the race
      await Promise.race([
        dispatch,
        new Promise<never>((_, reject) => {
          timer = setTimeout(
            () => reject(new Error(`Sync did not finish within ${MANUAL_SYNC_TIMEOUT_MS / 1000} seconds`)),
            MANUAL_SYNC_TIMEOUT_MS
          );
        }),
      ]);
    } catch (error) {
      setManualError(describeSyncError(error));
    } finally {
      clearTimeout(timer);
      setManualRunning(false);
    }
  };

  const syncing = activity.status === 'syncing' || manualRunning;

  return (
    <div className="space-y-6">
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h2 className="text-base font-semibold text-gray-900 mb-1">Sync status</h2>
        <p className="text-sm text-gray-600 mb-5">
          Mon Ami GO syncs automatically whenever you are connected. You never need to do anything to keep your work
          safe.
        </p>

        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <div>
            <dt className="text-gray-500">Connection</dt>
            <dd className="mt-1 flex items-center gap-2 font-medium text-gray-900" data-testid="sync-connection">
              {online ? (
                <>
                  <SignalIcon className="w-4 h-4 text-green-600" aria-hidden="true" /> Connected to Mon Ami
                </>
              ) : (
                <>
                  <SignalSlashIcon className="w-4 h-4 text-gray-400" aria-hidden="true" /> Offline
                </>
              )}
            </dd>
          </div>
          <div>
            <dt className="text-gray-500">Waiting to upload</dt>
            <dd className="mt-1 font-medium text-gray-900" data-testid="sync-pending">
              {pending === undefined ? '…' : describePending(pending.documents, pending.contacts, pending.notes)}
            </dd>
          </div>
          <div>
            <dt className="text-gray-500">Last successful sync</dt>
            <dd className="mt-1 font-medium text-gray-900" data-testid="sync-last-success">
              {formatWhen(activity.lastSuccessAt)}
            </dd>
          </div>
          <div>
            <dt className="text-gray-500">Right now</dt>
            <dd className="mt-1 flex items-center gap-2 font-medium text-gray-900" data-testid="sync-now-status">
              {syncing ? (
                <>
                  <ArrowPathIcon className="w-4 h-4 animate-spin text-deep-sea-green" aria-hidden="true" /> Syncing…
                </>
              ) : activity.lastError ? (
                <>
                  <ExclamationCircleIcon className="w-4 h-4 text-red-600" aria-hidden="true" /> Last attempt failed
                </>
              ) : activity.lastSuccessAt ? (
                <>
                  <CheckCircleIcon className="w-4 h-4 text-green-600" aria-hidden="true" /> Idle
                </>
              ) : (
                <>
                  <ClockIcon className="w-4 h-4 text-gray-400" aria-hidden="true" /> Waiting for the first sync
                </>
              )}
            </dd>
          </div>
        </dl>

        {activity.lastError && !manualError && (
          <div className="mt-5 bg-red-50 border border-red-200 rounded-md p-4" role="alert">
            <p className="text-sm font-medium text-red-800">The last automatic sync did not finish</p>
            <p className="mt-1 text-sm text-red-700">{describeSyncError(activity.lastError)}</p>
          </div>
        )}
      </div>

      {syncErrors.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-6" data-testid="sync-document-errors">
          <h2 className="text-base font-semibold text-gray-900 mb-1">Documents Mon Ami could not accept</h2>
          <p className="text-sm text-gray-600 mb-4">
            Mon Ami would not accept these documents, so their changes are still only on this device. Open each
            one, fix what Mon Ami names below, and it will sync on its own.
          </p>
          <ul className="divide-y divide-gray-100 border border-gray-200 rounded-md">
            {syncErrors.map((error) => (
              <li key={String(error.documentId)} className="p-4" data-testid="sync-document-error">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900">{error.documentName}</p>
                    <p className="text-xs text-gray-500 font-mono">
                      {error.clientSlug} · document {error.documentId}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {error.path && (
                      <Button variant="secondary" size="sm" onClick={() => navigate(error.path!)}>
                        Open document
                      </Button>
                    )}
                    <Button
                      variant="tertiary"
                      size="sm"
                      icon="arrow-rotate-left"
                      loading={retrying === error.documentId}
                      disabled={!online || retrying !== null}
                      onClick={() => void retryDocument(error.documentId)}
                    >
                      {retrying === error.documentId ? 'Trying…' : 'Try again'}
                    </Button>
                  </div>
                </div>
                <p className="mt-2 text-sm text-red-700">
                  {describeDocumentSyncError(error.message)}
                </p>
                {error.blocked && (
                  <p className="mt-1 text-xs text-gray-500">
                    Mon Ami has paused retrying this one — sending the same answer again would be rejected the same
                    way. It will sync as soon as the value changes, or press Try again.
                  </p>
                )}
                {retryOutcome?.id === error.documentId && (
                  <p className="mt-1 text-xs text-gray-700" role="status" data-testid="sync-retry-outcome">
                    {retryOutcome.message}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {noteErrors.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-6" data-testid="sync-note-errors">
          <h2 className="text-base font-semibold text-gray-900 mb-1">Notes Mon Ami could not accept</h2>
          <p className="text-sm text-gray-600 mb-4">
            These notes are still only on this device. Open each one, fix what Mon Ami names below, and save it to
            upload it again.
          </p>
          <ul className="divide-y divide-gray-100 border border-gray-200 rounded-md">
            {noteErrors.map((error) => (
              <li key={String(error.noteId)} className="p-4" data-testid="sync-note-error">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900">Note from {formatNoteDate(error.interactionDate)}</p>
                    <p className="text-xs text-gray-500 font-mono">{error.clientSlug}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button variant="secondary" size="sm" onClick={() => navigate(error.path)}>
                      Open note
                    </Button>
                    <Button
                      variant="tertiary"
                      size="sm"
                      icon="arrow-rotate-left"
                      loading={retrying === `note:${error.noteId}`}
                      disabled={!online || retrying !== null}
                      onClick={() => void retryNote(error.noteId)}
                    >
                      {retrying === `note:${error.noteId}` ? 'Trying…' : 'Try again'}
                    </Button>
                  </div>
                </div>
                <p className="mt-2 text-sm text-red-700">{error.message}</p>
                {retryOutcome?.id === `note:${error.noteId}` && (
                  <p className="mt-1 text-xs text-gray-700" role="status">
                    {retryOutcome.message}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h2 className="text-base font-semibold text-gray-900 mb-1">Having trouble?</h2>
        <p className="text-sm text-gray-600 mb-4">
          If something looks out of date and you are connected, you can run a sync right now. If it fails, the message
          below will explain why.
        </p>
        <div className="flex items-center gap-3">
          <Button variant="secondary" icon="arrow-rotate-left" onClick={syncNow} loading={manualRunning} disabled={!online || syncing}>
            {manualRunning ? 'Syncing…' : 'Sync now'}
          </Button>
          {!online && <span className="text-sm text-gray-500">Available when you are back online.</span>}
        </div>
        {manualError && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-4" role="alert" data-testid="sync-manual-error">
            <div className="flex items-start gap-3">
              <ExclamationCircleIcon className="w-5 h-5 text-red-600 flex-shrink-0" aria-hidden="true" />
              <p className="text-sm text-red-700">{manualError}</p>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h2 className="text-base font-semibold text-gray-900 mb-1">App updates</h2>
        <p className="text-sm text-gray-600 mb-4">
          This device is running build <span className="font-mono">{shortAppVersion()}</span>. New builds are picked up
          automatically; if someone reports not seeing a change, check here.
        </p>
        <div className="flex items-center gap-3">
          <Button
            variant="secondary"
            icon="arrow-rotate-left"
            onClick={checkForUpdates}
            loading={updateCheck === 'checking'}
            disabled={!online || updateCheck === 'checking'}
          >
            {updateCheck === 'checking' ? 'Checking…' : 'Check for app updates'}
          </Button>
          {updateCheck === 'update-available' && (
            <Button variant="primary" onClick={installUpdate} loading={reloading}>
              {reloading ? 'Reloading…' : 'Reload to update'}
            </Button>
          )}
        </div>
        <p className="mt-3 text-sm" data-testid="update-check-result">
          {updateCheck === 'up-to-date' && <span className="text-green-700">You are on the latest build.</span>}
          {updateCheck === 'update-available' && (
            <span className="text-gray-900">A newer build is ready. Reload to start using it.</span>
          )}
          {updateCheck === 'failed' && (
            <span className="text-red-700">Mon Ami could not be reached to check for updates. Try again when you are connected.</span>
          )}
          {updateCheck === 'unavailable' && (
            <span className="text-gray-500">Update checks are not available in this environment.</span>
          )}
        </p>
      </div>
    </div>
  );
}
